import React, { useState, useRef, useEffect } from 'react';
import { useChats } from './hooks/useChats';
import { useMessages } from './hooks/useMessages';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginForm from './components/Auth/LoginForm';
import { authFetch } from './utils/authFetch';
import Sidebar from './components/Navigation/Sidebar';
import ContactsView from './components/Contacts/ContactsView';
import SettingsView from './components/Settings/SettingsView';
import QuickRepliesView from './components/QuickReplies/QuickRepliesView';
import ReportsView from './components/Reports/ReportsView';
import TemplatesView from './components/Templates/TemplatesView';
import MessageBubble from './components/Message/MessageBubble';
import MessageInput from './components/Message/MessageInput';
import TypingIndicator from './components/Message/TypingIndicator';
import ChatAssignment from './components/Chat/ChatAssignment';
import NewMessage from './components/Chat/NewMessage';

interface Chat {
  id: string;
  contactId: string;
  assignedAgentId?: string;
  status: 'OPEN' | 'PENDING' | 'RESOLVED' | 'CLOSED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  subject?: string;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
  contact: {
    id: string;
    phoneNumber: string;
    name?: string;
    avatar?: string;
    isBlocked: boolean;
    tags: string[];
    notes?: string;
    createdAt: string;
    updatedAt: string;
  };
  messages?: any[];
  unreadCount?: number;
}

function MainApp() {
  const { user, logout } = useAuth();
  const { chats, loading, error, setChats, refetch: loadChats } = useChats();
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'mine' | 'unassigned' | 'all'>('mine');
  const [activeSection, setActiveSection] = useState<'inbox' | 'contacts' | 'quick-replies' | 'templates' | 'reports' | 'settings'>('inbox');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [chatContextMenu, setChatContextMenu] = useState<{
    x: number;
    y: number;
    chat: Chat;
  } | null>(null);
  const longPressTimeoutRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close context menu on outside click / escape / scroll
  useEffect(() => {
    if (!chatContextMenu) return;

    const handlePointerDown = () => setChatContextMenu(null);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setChatContextMenu(null);
    };
    const handleScroll = () => setChatContextMenu(null);

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [chatContextMenu]);

  // Close mobile sidebar when clicking outside
  const closeMobileSidebar = () => {
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  };

  // Handle chat selection on mobile
  const handleChatSelect = (chat: Chat) => {
    setSelectedChat(chat);
    if (isMobile) {
      // On mobile, hide conversations panel when chat is selected
    }
  };

  // Handle back button on mobile
  const handleMobileBack = () => {
    if (isMobile) {
      setSelectedChat(null);
    }
  };
  
  const { messages, loading: messagesLoading, sendMessage, isTyping, typingUserId, refetch: refetchMessages } = useMessages(
    selectedChat?.id || null, 
    user?.id
  );
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change or chat is selected
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    };

    // Scroll to bottom when messages load or new messages arrive
    if (!messagesLoading && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, messagesLoading, selectedChat?.id]);

  const openChatContextMenu = (chat: Chat, clientX: number, clientY: number) => {
    const MENU_WIDTH = 220;
    const MENU_HEIGHT = 52;
    const x = Math.max(8, Math.min(clientX, window.innerWidth - MENU_WIDTH - 8));
    const y = Math.max(8, Math.min(clientY, window.innerHeight - MENU_HEIGHT - 8));
    setChatContextMenu({ x, y, chat });
  };

  const filteredChats = chats.filter(chat => {
    // Hide closed chats from the list (history is preserved)
    if (chat.status === 'CLOSED') return false;

    const matchesSearch = chat.contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chat.contact.phoneNumber.includes(searchTerm);
    
    // Tab filtering
    let tabFiltered = true;
    if (activeTab === 'mine') {
      // Mine: Only chats assigned to current user
      tabFiltered = chat.assignedAgentId === user?.id;
    } else if (activeTab === 'unassigned') {
      // Unassigned: Only chats with no assignee
      tabFiltered = !chat.assignedAgentId;
    } else if (activeTab === 'all') {
      // All: Show all conversations regardless of assignment
      tabFiltered = true;
    }
    
    return matchesSearch && tabFiltered;
  });

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getTabCount = (tab: string) => {
    switch (tab) {
      case 'mine': return chats.filter(c => c.status !== 'CLOSED' && c.assignedAgentId === user?.id).length;
      case 'unassigned': return chats.filter(c => c.status !== 'CLOSED' && !c.assignedAgentId).length;
      case 'all': return chats.filter(c => c.status !== 'CLOSED').length;
      default: return 0;
    }
  };

  const totalUnreadCount = chats.reduce((sum, chat) => {
    if (chat.status === 'CLOSED') return sum;
    return sum + (chat.unreadCount || 0);
  }, 0);

  // Update browser tab title with unread count
  useEffect(() => {
    const loadTitle = async () => {
      try {
        const response = await authFetch('/settings/whatsapp');
        if (response.ok) {
          const data = await response.json();
          const businessName = data.success && data.data?.displayName 
            ? data.data.displayName 
            : 'WhatsApp Chat';
          const baseTitle = `${businessName} - WhatsApp Chat`;
          if (totalUnreadCount > 0) {
            document.title = `(${totalUnreadCount}) ${baseTitle}`;
          } else {
            document.title = baseTitle;
          }
        } else {
          document.title = totalUnreadCount > 0 
            ? `(${totalUnreadCount}) WhatsApp Chat` 
            : 'WhatsApp Chat';
        }
      } catch (error) {
        document.title = totalUnreadCount > 0 
          ? `(${totalUnreadCount}) WhatsApp Chat` 
          : 'WhatsApp Chat';
      }
    };
    
    if (user) {
      loadTitle();
    } else {
      document.title = 'WhatsApp Chat';
    }
  }, [totalUnreadCount, user]);

  const handleCloseChat = async (chat: Chat) => {
    try {
      const response = await authFetch(`/chat/${chat.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'CLOSED' })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error || 'Failed to close chat');
      }

      // Update local state so it disappears from the list immediately
      setChats(prev =>
        prev.map(c => (c.id === chat.id ? { ...c, status: 'CLOSED' } : c))
      );

      if (selectedChat?.id === chat.id) {
        setSelectedChat(null);
      }
    } catch (e) {
      console.error('Failed to close chat:', e);
      alert(e instanceof Error ? e.message : 'Failed to close chat');
    } finally {
      setChatContextMenu(null);
    }
  };

  const handleAssignChat = async (agentId: string | null) => {
    if (!selectedChat) return;
    
    try {
      // Update local state immediately for better UX
      setSelectedChat({
        ...selectedChat,
        assignedAgentId: agentId || undefined
      });

      // Call API to update assignment in database
      // authFetch automatically prepends /api
      const response = await authFetch(`/chat/${selectedChat.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assignedAgentId: agentId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Chat assignment updated:', data);
        
        // Update the chats list with the response from server
        const updatedChats = chats.map(chat => 
          chat.id === selectedChat.id 
            ? { ...chat, assignedAgentId: agentId || undefined }
            : chat
        );
        setChats(updatedChats);
        
        alert(`Conversation ${agentId ? 'assigned successfully' : 'unassigned successfully'}`);
      } else {
        throw new Error('Failed to update assignment');
      }
      
    } catch (error) {
      console.error('Failed to assign chat:', error);
      alert('Failed to assign conversation');
      
      // Revert local state on error
      setSelectedChat({
        ...selectedChat,
        assignedAgentId: selectedChat.assignedAgentId
      });
    }
  };

  const handleResolveChat = async () => {
    if (!selectedChat) return;
    
    try {
      setSelectedChat({
        ...selectedChat,
        status: 'RESOLVED'
      });
      
      console.log(`Resolving chat ${selectedChat.id}`);
      alert('Conversation marked as resolved');
    } catch (error) {
      console.error('Failed to resolve chat:', error);
      alert('Failed to resolve conversation');
    }
  };

  const handleStartNewChat = async (phoneNumber: string, contactName?: string) => {
    try {
      // Create or find contact
      // authFetch automatically prepends /api
      const response = await authFetch('/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber,
          contactName
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Reload chats to show the new chat
          loadChats();
          // Select the new chat
          setSelectedChat(data.data);
          setShowNewMessageModal(false);
          alert('New conversation started!');
        }
      } else {
        throw new Error('Failed to create chat');
      }
    } catch (error) {
      console.error('Failed to start new chat:', error);
      alert('Failed to start new conversation');
    }
  };

  const renderInboxView = () => (
    <div className="content-area">
      <div className={`conversations-panel ${isMobile && selectedChat ? 'mobile-hidden' : ''}`}>
        <div className="panel-header">
          <div>
            <h5 className="panel-title">Conversations</h5>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {loading ? 'Loading...' : `${chats.length} total`}
            </div>
          </div>
          <button 
            className="new-message-button"
            onClick={() => setShowNewMessageModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            + New Message
          </button>
        </div>
        
        <div className="panel-tabs">
          {[
            { key: 'mine', label: 'Mine' },
            { key: 'unassigned', label: 'Unassigned' },
            { key: 'all', label: 'All' }
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`panel-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key as any)}
            >
              {label} {getTabCount(key)}
            </button>
          ))}
        </div>

        <div className="search-container">
          <input
            type="text"
            placeholder="Search conversations..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="conversation-list">
          {loading && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 8px' }}></div>
              Loading conversations...
            </div>
          )}
          
          {error && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#f59e0b' }}>
              <div style={{ fontSize: '12px', marginBottom: '4px' }}>Demo Mode</div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>{error}</div>
            </div>
          )}
          
          {!loading && filteredChats.map((chat) => (
            <div
              key={chat.id}
              className={`conversation-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
              onClick={() => {
                if (longPressTriggeredRef.current) {
                  longPressTriggeredRef.current = false;
                  return;
                }
                handleChatSelect(chat);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                openChatContextMenu(chat, e.clientX, e.clientY);
              }}
              onTouchStart={(e) => {
                if (!isMobile) return;
                longPressTriggeredRef.current = false;
                if (longPressTimeoutRef.current) {
                  window.clearTimeout(longPressTimeoutRef.current);
                }
                const touch = e.touches[0];
                longPressTimeoutRef.current = window.setTimeout(() => {
                  longPressTriggeredRef.current = true;
                  openChatContextMenu(chat, touch.clientX, touch.clientY);
                }, 600);
              }}
              onTouchEnd={() => {
                if (longPressTimeoutRef.current) {
                  window.clearTimeout(longPressTimeoutRef.current);
                  longPressTimeoutRef.current = null;
                }
              }}
              onTouchCancel={() => {
                if (longPressTimeoutRef.current) {
                  window.clearTimeout(longPressTimeoutRef.current);
                  longPressTimeoutRef.current = null;
                }
              }}
            >
              <div className="avatar" style={{ position: 'relative' }}>
                {chat.contact.avatar ? (
                  <img 
                    src={chat.contact.avatar} 
                    alt={chat.contact.name || chat.contact.phoneNumber}
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  getInitials(chat.contact.name || chat.contact.phoneNumber)
                )}
              </div>
              <div className="conversation-content">
                <div className="conversation-header">
                  <div className="contact-name" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {chat.contact.name || chat.contact.phoneNumber}
                    {(chat.unreadCount || 0) > 0 && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#10b981',
                        borderRadius: '50%',
                        flexShrink: 0
                      }} />
                    )}
                  </div>
                  <div className="message-time">
                    {formatTime(chat.lastMessageAt)}
                  </div>
                </div>
                <div className="last-message" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    fontWeight: (chat.unreadCount || 0) > 0 ? '600' : '400'
                  }}>
                    {chat.messages?.[chat.messages.length - 1]?.content || 'No messages yet'}
                  </span>
                  {(chat.unreadCount || 0) > 0 && (
                    <span style={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      borderRadius: '12px',
                      padding: '2px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      minWidth: '20px',
                      textAlign: 'center',
                      flexShrink: 0
                    }}>
                      {(chat.unreadCount || 0) > 99 ? '99+' : (chat.unreadCount || 0)}
                    </span>
                  )}
                </div>
                <div className="conversation-meta">
                  <span className={`status-badge status-${chat.status.toLowerCase()}`}>
                    {chat.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="chat-panel">
        {selectedChat ? (
          <>
            <div className="chat-header">
              {/* Mobile back button */}
              {isMobile && (
                <button
                  onClick={handleMobileBack}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0 8px',
                    color: '#374151'
                  }}
                >
                  ←
                </button>
              )}
              <div className="chat-header-info">
                <div className="avatar" style={{ marginRight: '12px' }}>
                  {selectedChat.contact.avatar ? (
                    <img 
                      src={selectedChat.contact.avatar} 
                      alt={selectedChat.contact.name || selectedChat.contact.phoneNumber}
                    />
                  ) : (
                    getInitials(selectedChat.contact.name || selectedChat.contact.phoneNumber)
                  )}
                </div>
                <div>
                  <h6 style={{ margin: '0', fontSize: '16px', fontWeight: '600' }}>
                    {selectedChat.contact.name || selectedChat.contact.phoneNumber}
                  </h6>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    {selectedChat.contact.phoneNumber} • {selectedChat.status}
                  </div>
                </div>
              </div>
              <div className="chat-header-actions">
                <button 
                  className="action-button"
                  onClick={handleResolveChat}
                >
                  Resolve
                </button>
                <button 
                  className="action-button primary"
                  onClick={() => setShowAssignmentModal(true)}
                >
                  Assign
                </button>
              </div>
            </div>

            <div className="messages-container" ref={messagesContainerRef}>
              {messagesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
                  Loading messages...
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                  <h4>Start the conversation</h4>
                  <p>Send a message to begin chatting</p>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <MessageBubble key={message.id} message={message} />
                  ))}
                  {isTyping && <TypingIndicator userName={selectedChat.contact.name} />}
                </>
              )}
            </div>

            <MessageInput 
              onSendMessage={sendMessage}
              disabled={messagesLoading}
              chatId={selectedChat.id}
              onTemplateSent={() => {
                refetchMessages();
                loadChats();
              }}
            />
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state-content">
              <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.3 }}>💬</div>
              <h4>Select a conversation</h4>
              <p>Choose a conversation from the left sidebar to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {showNewMessageModal && (
        <NewMessage
          onClose={() => setShowNewMessageModal(false)}
          onStartChat={handleStartNewChat}
        />
      )}

      {chatContextMenu && (
        <div
          style={{
            position: 'fixed',
            left: chatContextMenu.x,
            top: chatContextMenu.y,
            width: 220,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
            zIndex: 3000,
            overflow: 'hidden'
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              if (window.confirm('Close this chat? It will be removed from the list but history is preserved.')) {
                handleCloseChat(chatContextMenu.chat);
              } else {
                setChatContextMenu(null);
              }
            }}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '12px 14px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              color: '#b91c1c',
              fontWeight: 600
            }}
          >
            Close chat
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="app-container">
      {/* Mobile overlay */}
      {isMobile && isMobileSidebarOpen && (
        <div className="mobile-overlay active" onClick={closeMobileSidebar} />
      )}
      
      {/* Mobile menu button - show on all sections except when chat is open */}
      {isMobile && (activeSection !== 'inbox' || !selectedChat) && (
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            zIndex: 1001,
            background: '#1f2937',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '56px',
            height: '56px',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ☰
        </button>
      )}
      
      <Sidebar 
        activeSection={activeSection}
        onSectionChange={(section) => {
          setActiveSection(section);
          closeMobileSidebar();
        }}
        unreadCount={totalUnreadCount}
        className={isMobileSidebarOpen ? 'mobile-open' : ''}
      />
      {activeSection === 'inbox' && renderInboxView()}
      {activeSection === 'contacts' && <ContactsView />}
      {activeSection === 'quick-replies' && <QuickRepliesView />}
      {activeSection === 'templates' && <TemplatesView />}
      {activeSection === 'reports' && <ReportsView />}
      {activeSection === 'settings' && <SettingsView />}
      
      {showAssignmentModal && selectedChat && (
        <ChatAssignment
          chatId={selectedChat.id}
          currentAssigneeId={selectedChat.assignedAgentId}
          onAssign={handleAssignChat}
          onClose={() => setShowAssignmentModal(false)}
        />
      )}
    </div>
  );
}

function App() {
  const { user, login, loading, error } = useAuth();

  if (!user) {
    return <LoginForm onLogin={login} loading={loading} error={error} />;
  }

  return <MainApp />;
}

function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithAuth;
